/**
 * Hook para gerenciamento de categorias de produtos CRM
 * Fornece queries e mutations para CRUD de categorias
 *
 * Versão: 1.3.0
 * - Read da listagem migrado para RPC server-side
 *   (get_tenant_product_categories) — elimina SELECT direto em
 *   crm_product_categories e crm_category_workspaces.
 * - Mutations (create/update/delete) preservadas (escopo cirúrgico).
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

// Tipo para categoria de produto
export interface CrmProductCategory {
  id: string;
  tenant_id: string;
  parent_id: string | null;
  name: string;
  description: string | null;
  icon: string;
  color: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  children?: CrmProductCategory[];
}

// Tipo para criação de categoria
export interface CreateCategoryInput {
  name: string;
  description?: string;
  parent_id?: string | null;
  icon?: string;
  color?: string;
  sort_order?: number;
}

// Tipo para atualização de categoria
export interface UpdateCategoryInput {
  id: string;
  name?: string;
  description?: string | null;
  parent_id?: string | null;
  icon?: string;
  color?: string;
  sort_order?: number;
  is_active?: boolean;
}

// Opções para configurar o hook
export interface CrmCategoriesOptions {
  /** Filtrar por workspace (via tabela de junção crm_category_workspaces) */
  workspaceId?: string | null;
}

/**
 * Hook principal para categorias CRM
 */
export function useCrmCategories(options?: CrmCategoriesOptions) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // Query para listar categorias — agora 100% server-side via RPC
  const categoriesQuery = useQuery({
    queryKey: ['crm-categories', currentTenant?.id, options?.workspaceId],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      const { data, error } = await (supabase.rpc as any)(
        'get_tenant_product_categories',
        {
          p_workspace_id: options?.workspaceId ?? null,
          p_tenant_hint: currentTenant.id,
        },
      );

      if (error) throw error;
      return (data || []) as CrmProductCategory[];
    },
    enabled: !!currentTenant?.id,
  });

  // Função para organizar categorias em árvore hierárquica
  const buildCategoryTree = (categories: CrmProductCategory[]): CrmProductCategory[] => {
    const categoryMap = new Map<string, CrmProductCategory>();
    const rootCategories: CrmProductCategory[] = [];

    // Criar mapa de categorias
    categories.forEach(cat => {
      categoryMap.set(cat.id, { ...cat, children: [] });
    });

    // Organizar hierarquia
    categories.forEach(cat => {
      const category = categoryMap.get(cat.id)!;
      if (cat.parent_id && categoryMap.has(cat.parent_id)) {
        categoryMap.get(cat.parent_id)!.children!.push(category);
      } else {
        rootCategories.push(category);
      }
    });

    return rootCategories;
  };

  // Mutation para criar categoria
  const createCategory = useMutation({
    mutationFn: async (input: CreateCategoryInput) => {
      if (!currentTenant?.id) throw new Error('Tenant não encontrado');

      const { data, error } = await supabase
        .from('crm_product_categories')
        .insert({
          tenant_id: currentTenant.id,
          name: input.name,
          description: input.description || null,
          parent_id: input.parent_id || null,
          icon: input.icon || 'folder',
          color: input.color || '#6366f1',
          sort_order: input.sort_order || 0,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-categories'] });
      toast.success('Categoria criada com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar categoria: ${error.message}`);
    },
  });

  // Mutation para atualizar categoria
  const updateCategory = useMutation({
    mutationFn: async (input: UpdateCategoryInput) => {
      const { id, ...updates } = input;

      const { data, error } = await supabase
        .from('crm_product_categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-categories'] });
      toast.success('Categoria atualizada com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar categoria: ${error.message}`);
    },
  });

  // Mutation para deletar categoria
  const deleteCategory = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('crm_product_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-categories'] });
      toast.success('Categoria excluída com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir categoria: ${error.message}`);
    },
  });

  // Memoizar categoryTree para evitar recálculos a cada render
  // Isso corrige o bug de expansão/retração das categorias
  const categoryTree = React.useMemo(
    () => buildCategoryTree(categoriesQuery.data || []),
    [categoriesQuery.data]
  );

  /**
   * Coleta recursivamente todos os IDs filhos de uma categoria
   */
  const collectAllChildIds = React.useCallback((category: CrmProductCategory, ids: string[]) => {
    if (category.children) {
      for (const child of category.children) {
        ids.push(child.id);
        collectAllChildIds(child, ids);
      }
    }
  }, []);

  /**
   * Coleta o ID da categoria selecionada + todos os IDs das subcategorias
   * Usado para filtrar produtos de uma categoria incluindo suas subcategorias
   */
  const getCategoryWithDescendantIds = React.useCallback((targetId: string): string[] => {
    const ids: string[] = [];
    
    const findAndCollect = (cats: CrmProductCategory[]): boolean => {
      for (const cat of cats) {
        if (cat.id === targetId) {
          // Encontrou a categoria alvo, coletar ela e todos os filhos
          ids.push(cat.id);
          collectAllChildIds(cat, ids);
          return true;
        }
        if (cat.children && findAndCollect(cat.children)) {
          return true;
        }
      }
      return false;
    };
    
    findAndCollect(categoryTree);
    return ids;
  }, [categoryTree, collectAllChildIds]);

  return {
    categories: categoriesQuery.data || [],
    categoryTree,
    /** Retorna IDs da categoria + todas as subcategorias descendentes */
    getCategoryWithDescendantIds,
    isLoading: categoriesQuery.isLoading,
    error: categoriesQuery.error,
    createCategory,
    updateCategory,
    deleteCategory,
    refetch: categoriesQuery.refetch,
  };
}
